import theme from "@shared/utils/themeColors";
import React from "react";
import LegalPageShell from "../components/LegalPageShell";

const sectionTitle = `text-xl md:text-2xl font-semibold mt-8 mb-4 ${theme.text.primary}`;
const subTitle = `text-lg font-semibold mt-6 mb-3 ${theme.text.primary}`;
const paragraph = `mb-4 leading-relaxed ${theme.text.secondary}`;
const listStyle = `list-disc pl-6 mb-4 space-y-1 ${theme.text.secondary}`;
const link = `${theme.text.link} hover:underline`;

const PrivacyPolicyPage: React.FC = () => {
  return (
    <LegalPageShell title="Privacy Policy">
      {/* Meta */}
      <p className={`text-sm mb-1 font-semibold ${theme.text.brand}`}>
        Global Colon Cancer Association (GCCA)
      </p>
      <p className={`text-sm mb-1 ${theme.text.secondary}`}>Membership Hub</p>
      <p className={`text-xs mb-8 ${theme.text.muted}`}>
        Effective Date: 3/13/2026 &middot; Last Updated: 3/13/2026
      </p>

      {/* 1 */}
      <h2 className={sectionTitle}>1. Introduction</h2>
      <p className={paragraph}>
        The Global Colon Cancer Association ("GCCA," "we," "us," or "our") is
        committed to protecting your privacy. This Privacy Policy describes how
        we collect, use, store, and share personal information when you use our
        Membership Hub (the "Platform"). It also explains your rights regarding
        your personal data.
      </p>
      <p className={paragraph}>
        By using the Platform, you agree to the collection and use of
        information in accordance with this Privacy Policy. This Policy
        supplements and updates the general GCCA privacy policy available at{" "}
        <a
          href="https://www.globalcca.org"
          target="_blank"
          rel="noopener noreferrer"
          className={link}
        >
          GLOBALCCA.ORG
        </a>
        .
      </p>

      {/* 2 */}
      <h2 className={sectionTitle}>2. Information We Collect</h2>

      <h3 className={subTitle}>2.1 Information You Provide Directly</h3>
      <p className={paragraph}>
        When you register for an account or participate in Platform activities,
        we may collect:
      </p>
      <ul className={listStyle}>
        <li>Full name</li>
        <li>Email address and other contact details</li>
        <li>
          Demographic information (e.g., age, city, state/province, country)
        </li>
        <li>Forum posts, comments, and event-related submissions</li>
      </ul>
      <p className={paragraph}>
        Submission of personal information is voluntary. However, certain
        features of the Platform (such as account creation or event
        registration) may require you to provide information to participate.
      </p>

      <h3 className={subTitle}>2.2 Information Collected Automatically</h3>
      <p className={paragraph}>
        When you access the Platform, we may automatically collect certain
        technical data, including:
      </p>
      <ul className={listStyle}>
        <li>IP address</li>
        <li>Browser type and version</li>
        <li>Pages visited and time spent on the Platform</li>
        <li>Referring URLs</li>
      </ul>
      <p className={paragraph}>
        This data is used to analyze trends, maintain security, and improve
        Platform performance. It is not linked to personally identifiable
        information.
      </p>

      <h3 className={subTitle}>2.3 Cookies</h3>
      <p className={paragraph}>
        We use cookies and similar tracking technologies to enhance your
        experience on the Platform. Cookies are small data files stored on your
        device. We use cookies for session management and to gather aggregate
        usage statistics. You may configure your browser to decline cookies, but
        doing so may limit certain Platform functionality. Third-party partners
        and sponsors may also use their own cookies, which are outside our
        control.
      </p>

      {/* 3 */}
      <h2 className={sectionTitle}>3. How We Use Your Information</h2>
      <p className={paragraph}>
        We use your personal information for the following purposes:
      </p>
      <ul className={listStyle}>
        <li>To create and manage your member account</li>
        <li>To send communications you have opted in to receive</li>
        <li>To register you for events and send related updates</li>
        <li>To facilitate community forum participation</li>
        <li>To provide access to educational resources and content</li>
        <li>
          To analyze aggregate Platform usage and improve our services
        </li>
        <li>To respond to your inquiries and support requests</li>
        <li>To comply with applicable legal obligations</li>
      </ul>

      {/* 4 */}
      <h2 className={sectionTitle}>
        4. Legal Bases for Processing (GDPR)
      </h2>
      <p className={paragraph}>
        If you are located in the European Economic Area (EEA) or United
        Kingdom, our legal bases for processing your personal data include:
      </p>
      <ul className={listStyle}>
        <li>
          <strong>Consent:</strong> Where you have given explicit consent (e.g.,
          opting in to communications)
        </li>
        <li>
          <strong>Contract:</strong> Where processing is necessary to fulfill
          our membership services
        </li>
        <li>
          <strong>Legitimate Interests:</strong> Where processing is in our
          legitimate organizational interests and does not override your rights
        </li>
        <li>
          <strong>Legal Obligation:</strong> Where processing is required by
          applicable law
        </li>
      </ul>

      {/* 5 */}
      <h2 className={sectionTitle}>5. Sharing Your Information</h2>

      <h3 className={subTitle}>5.1 Service Providers</h3>
      <p className={paragraph}>
        We may share your information with trusted third-party vendors who
        assist us in operating the Platform, delivering communications, or
        providing services (e.g., email delivery platforms, event management
        tools). These vendors are contractually required to protect your
        information and use it only for the purposes for which it was shared.
      </p>

      <h3 className={subTitle}>5.2 Aggregated Data</h3>
      <p className={paragraph}>
        We may share aggregated, anonymized demographic statistics with partners
        and sponsors. This information cannot be used to identify any
        individual.
      </p>

      <h3 className={subTitle}>5.3 Legal Requirements</h3>
      <p className={paragraph}>
        We may disclose your personal information if required to do so by law,
        court order, or valid governmental request, or to protect the rights,
        property, or safety of GCCA, its members, or the public.
      </p>

      <h3 className={subTitle}>5.4 No Sale of Data</h3>
      <p className={paragraph}>
        GCCA does not sell, rent, or trade your personal information to third
        parties for their marketing purposes.
      </p>

      {/* 6 */}
      <h2 className={sectionTitle}>6. Data Retention</h2>
      <p className={paragraph}>
        We retain your personal information only as long as necessary to fulfill
        the purposes described in this Privacy Policy, or as required by
        applicable law. When your data is no longer needed, we will securely
        delete or anonymize it. You may request deletion of your data at any
        time (see Section 9).
      </p>

      {/* 7 */}
      <h2 className={sectionTitle}>7. Data Security</h2>
      <p className={paragraph}>
        GCCA implements reasonable technical and organizational measures to
        protect your personal information from unauthorized access, use,
        alteration, or disclosure. Our servers are maintained in a secure
        environment. We also maintain contractual data protection agreements
        with our third-party technology providers.
      </p>
      <p className={paragraph}>
        Please note, however, that no method of transmission over the internet
        or electronic storage is completely secure. While we strive to protect
        your information, we cannot guarantee absolute security. By using the
        Platform, you acknowledge this inherent risk.
      </p>
      <p className={paragraph}>
        In the event of a data breach that compromises your personal
        information, GCCA will notify affected individuals promptly, in
        accordance with applicable law and notification procedures.
      </p>

      {/* 8 */}
      <h2 className={sectionTitle}>8. Children's Privacy</h2>
      <p className={paragraph}>
        The Platform is not directed at children under the age of 13. We do not
        knowingly collect personal information from children under 13 without
        verifiable parental consent. If we become aware that a child under 13
        has provided us with personal information without consent, we will
        delete it promptly. If you believe a child has provided us with
        information without consent, please contact us at{" "}
        <a href="mailto:privacy@globalcca.org" className={link}>
          privacy@globalcca.org
        </a>
        .
      </p>

      {/* 9 */}
      <h2 className={sectionTitle}>9. Your Privacy Rights</h2>
      <p className={paragraph}>
        Depending on your jurisdiction, you may have the following rights
        regarding your personal data:
      </p>

      <h3 className={subTitle}>9.1 All Users</h3>
      <ul className={listStyle}>
        <li>
          <strong>Access:</strong> Request a copy of the personal information we
          hold about you
        </li>
        <li>
          <strong>Correction:</strong> Request that inaccurate or incomplete
          data be corrected
        </li>
        <li>
          <strong>Deletion:</strong> Request that your personal data be deleted
          (subject to legal obligations)
        </li>
        <li>
          <strong>Opt-Out:</strong> Unsubscribe from marketing communications at
          any time
        </li>
      </ul>

      <h3 className={subTitle}>9.2 EEA/UK Users (GDPR)</h3>
      <ul className={listStyle}>
        <li>
          <strong>Portability:</strong> Request your data in a structured,
          machine-readable format
        </li>
        <li>
          <strong>Restriction:</strong> Request that we limit processing of your
          data in certain circumstances
        </li>
        <li>
          <strong>Objection:</strong> Object to processing based on legitimate
          interests
        </li>
        <li>
          <strong>Withdraw Consent:</strong> Where processing is based on
          consent, you may withdraw it at any time without affecting prior
          lawful processing
        </li>
      </ul>
      <p className={paragraph}>
        To exercise your rights, please contact us at{" "}
        <a href="mailto:privacy@globalcca.org" className={link}>
          privacy@globalcca.org
        </a>
        . We will respond within 30 days, or within the timeframe required by
        applicable law.
      </p>

      <h3 className={subTitle}>9.3 California Residents (CCPA)</h3>
      <p className={paragraph}>
        If you are a California resident, you have the right to know what
        personal information we collect, to request deletion of your personal
        information, to opt out of any sale of personal information (note: GCCA
        does not sell personal information), and to non-discrimination for
        exercising your privacy rights. To submit a request, contact us at{" "}
        <a href="mailto:privacy@globalcca.org" className={link}>
          privacy@globalcca.org
        </a>
        .
      </p>

      {/* 10 */}
      <h2 className={sectionTitle}>10. International Data Transfers</h2>
      <p className={paragraph}>
        GCCA is based in the United States. If you are accessing the Platform
        from outside the United States, please be aware that your information
        may be transferred to and processed in the United States, where data
        protection laws may differ from those in your country. Where required by
        law (e.g., GDPR), we implement appropriate safeguards such as Standard
        Contractual Clauses for such transfers.
      </p>

      {/* 11 */}
      <h2 className={sectionTitle}>11. Public Forums</h2>
      <p className={paragraph}>
        Any information you share in the Platform's public areas, such as
        discussion boards or community posts, becomes publicly visible to other
        registered members. We advise you to exercise caution when deciding to
        share personal information in these spaces. GCCA is not responsible for
        the use of information voluntarily disclosed in public areas.
      </p>

      {/* 12 */}
      <h2 className={sectionTitle}>12. Links to Third-Party Sites</h2>
      <p className={paragraph}>
        The Platform may contain links to external websites. Once you leave our
        Platform, your use of any external site is governed by that site's
        privacy policy. GCCA is not responsible for the privacy practices of
        third-party sites and encourages you to review their policies before
        submitting personal information.
      </p>

      {/* 13 */}
      <h2 className={sectionTitle}>
        13. Changes to This Privacy Policy
      </h2>
      <p className={paragraph}>
        GCCA reserves the right to update or modify this Privacy Policy at any
        time. Material changes will be communicated to registered members via
        email or a prominent notice on the Platform. The revised policy will
        take effect upon posting, and your continued use of the Platform
        constitutes your acceptance of the updated terms.
      </p>

      {/* 14 */}
      <h2 className={sectionTitle}>14. Contact Us</h2>
      <p className={paragraph}>
        For questions, concerns, or requests regarding this Privacy Policy or
        your personal data, please contact:
      </p>
      <p className={`mb-1 font-semibold ${theme.text.primary}`}>
        Global Colon Cancer Association &mdash; Privacy Office
      </p>
      <p className={paragraph}>
        Email:{" "}
        <a href="mailto:privacy@globalcca.org" className={link}>
          privacy@globalcca.org
        </a>
      </p>
      <p className={paragraph}>
        Website:{" "}
        <a
          href="https://www.globalcca.org"
          target="_blank"
          rel="noopener noreferrer"
          className={link}
        >
          GLOBALCCA.ORG
        </a>
      </p>
      <p className={paragraph}>
        Mailing Address: 5335 Wisconsin Ave NW, Suite 440, Washington, DC 20015
        USA
      </p>
    </LegalPageShell>
  );
};

export default PrivacyPolicyPage;
